/**
 * READING THE NAMES OFF AN FIC NOTICE.
 *
 * The founder, 13 September 2026:
 *
 *   "Because sanctioned persons arrive from the FIC in pdf format, I want to be
 *    able to upload that pdf, and have that part of the portal extract the
 *    named individuals."
 *
 * ---------------------------------------------------------------------------
 * THIS PRODUCES A PROPOSAL, NOT A LISTING, AND THAT IS THE WHOLE DESIGN
 *
 * A false name on the sanctions list holds a real person's account. A PDF parse
 * is a guess about where the words are on a page — it has no idea what a name
 * is, and a notice laid out in two columns, or with a letterhead, or with a
 * footer on every page, will hand back lines that are not people.
 *
 * So nothing here writes anything. It reads the document, proposes what it
 * thinks are people, and the screen shows every one of them to a compliance
 * officer who ticks, edits and untickes before a single row is written. The
 * database agrees: `fn_add_sanctions_list_entries` takes NAMES and has no idea
 * a PDF was ever involved.
 *
 * The division, stated once:
 *
 *     this file      guesses, and says it is guessing
 *     the screen     shows every guess, and lets each one be changed
 *     the person     decides
 *     the database   records
 *
 * ---------------------------------------------------------------------------
 * THE EXTRACTION IS DELIBERATELY CAUTIOUS IN ONE DIRECTION ONLY
 *
 * Missing a name costs a second look at the document. Inventing one costs a
 * person their account. Those are not symmetrical, so every rule below that had
 * to choose leant towards proposing FEWER lines — and the screen carries an
 * "add a name the extraction missed" field precisely because that is the
 * failure this is tuned to have.
 *
 * WHAT IS NOT CLAIMED HERE. I have not had a real FIC notice to read. These
 * heuristics come from the shape of the documents the FIC publishes — a
 * heading, a reference, then names, frequently in capitals — and from the
 * general shape of a PDF text layer. They are honest guesses and they are
 * marked as such in the interface, not presented as parsing.
 *
 * ---------------------------------------------------------------------------
 * PDF.JS IS LOADED ONLY WHEN SOMEBODY OPENS THIS
 *
 * It is ~100 KB gzipped and used by one screen that a handful of compliance
 * seats ever open. A static import puts it in the main bundle for every person
 * who signs into the Portal to look at a queue. The dynamic import below makes
 * webpack split it into its own chunk, fetched the first time a notice is
 * actually read.
 *
 * AND THE WORKER COMES FROM `pdf.worker.entry`, not from a CDN and not from a
 * blob. The Portal's CSP is `script-src 'self'` with no worker-src, so a worker
 * falls back to that: a CDN URL is refused and so is a blob: URL. The entry
 * module is bundled by webpack and served from this origin, which is the one
 * arrangement that satisfies the policy — and it cannot drift out of version
 * with the API, because it is the same package.
 */

/** Lines the FIC prints on every notice that are not people. */
const BOILERPLATE = [
  'financial intelligence centre',
  'targeted financial sanctions',
  'united nations security council',
  'security council',
  'resolution',
  'page',
  'notice',
  'schedule',
  'annexure',
  'the following persons',
  'the following individuals',
  'date of birth',
  'passport',
  'identity number',
  'nationality',
  'designation',
  'listed on',
  'republic of south africa',
  'government gazette',
  'in terms of section',
  // SECTION HEADINGS, ADDED AFTER READING A REAL NOTICE. TFS-922's fallback
  // proposals were "A. Individuals" and "Individuals Changes" -- both headings,
  // both two capitalised words, both indistinguishable from a person by any
  // rule about capitalisation. The structured reader does not see them at all;
  // this keeps them out of the fallback too.
  'individuals',
  'entities',
  'changes',
  'amendment',
  'no.',
];

const TITLES = ['mr', 'mrs', 'ms', 'miss', 'dr', 'prof', 'adv', 'rev', 'sir', 'hon'];

/**
 * NAME PARTICLES, WHICH ARE LOWER CASE AND ARE STILL PART OF THE NAME.
 *
 * THIS LIST EXISTS BECAUSE ITS ABSENCE WAS A REAL DEFECT, AND A BAD ONE HERE.
 * The capitalisation rule below began as "every word must start with a capital",
 * which is true of most names and false of a great many South African ones:
 * van der Merwe, van Zyl, du Plessis, du Toit, de Wet, de Villiers. Every one
 * of those would have been dropped from a notice silently — so a sanctions list
 * built by this tool would have had a systematic hole shaped like Afrikaans
 * surnames, and nobody would have seen it because the failure is a name that
 * never appears.
 *
 * Caught by the test that asserts "Jan van der Merwe" is a person.
 */
const PARTICLES = [
  'van', 'von', 'der', 'den', 'de', 'del', 'della', 'di', 'da', 'do', 'dos',
  'das', 'du', 'la', 'le', 'los', 'las', 'ter', 'ten', 'op', 'bin', 'ibn',
  'al', 'el', 'ben', 'abu', 'af', 'av', 'y', 'e',
];

/**
 * Is this line plausibly a person's name?
 *
 * Every rejection below is a line type that has actually turned up in a text
 * layer, and each one would otherwise have become an ACTIVE SANCTIONS LISTING.
 */
export function looksLikeAName(raw) {
  const line = String(raw || '').trim();
  if (!line) return false;

  // A page number, a reference, a date, a row of dots or dashes.
  if (!/[A-Za-z]/.test(line)) return false;
  // Mostly not letters — a table row, a reference code, an address.
  const letters = (line.match(/[A-Za-z]/g) || []).length;
  if (letters / line.length < 0.6) return false;

  const lower = line.toLowerCase();
  if (BOILERPLATE.some((b) => lower.includes(b))) return false;

  // A sentence, not a name. Names do not end in a full stop or carry a colon,
  // and they do not run to fifteen words.
  if (/[:;]/.test(line)) return false;
  if (/\.\s*$/.test(line) && !/^[A-Z]\.\s*$/.test(line)) return false;

  const words = line.split(/\s+/).filter(Boolean);
  if (words.length < 2 || words.length > 6) return false;

  // Every word must read like a name part: letters, possibly hyphenated or
  // apostrophed, possibly an initial. One stray word with digits in it and the
  // line is a table row.
  const namey = words.every((w) =>
    /^[A-Za-z][A-Za-z'’-]*\.?$/.test(w) || /^[A-Z]\.$/.test(w));
  if (!namey) return false;

  // AND IT MUST BE CAPITALISED LIKE A NAME. A lower-case line in a text layer
  // is running prose, not an entry. ALL CAPS is how the FIC prints them, and
  // Title Case is how most other sources do.
  //
  // PARTICLES ARE EXEMPT AND ARE STILL PART OF THE NAME — see PARTICLES above,
  // which exists because requiring a capital on every word quietly dropped
  // every van der Merwe and du Plessis from every notice.
  const bare = (w) => w.replace(/\./g, '').toLowerCase();
  const realWords = words.filter((w) => !TITLES.includes(bare(w)));
  if (realWords.length < 2) return false;

  const named = realWords.filter((w) => !PARTICLES.includes(bare(w)));
  // At least two capitalised parts that are not particles: "de la" is not a
  // person, and neither is a line that is all particles.
  if (named.length < 2) return false;
  if (!named.every((w) => /^[A-Z]/.test(w))) return false;

  return true;
}

/**
 * Turn a page of extracted text into proposed people.
 *
 * Deduplicated case-insensitively, because a notice that names somebody in a
 * heading and again in a table would otherwise propose them twice and the
 * officer would have to notice that themselves.
 */
/**
 * THE REAL NOTICE, MEASURED — and the heuristics above did not read it.
 *
 * The founder sent an actual FIC notice on 13 September 2026, TFS-922, the
 * first this code has ever been run against. The result, measured rather than
 * reasoned about:
 *
 *     reference   TFS-922-05/09/2026     correct
 *     names       "A. Individuals"       a section heading
 *                 "Individuals Changes"  another section heading
 *     missing     SANAULLAH GHAFARI      the one sanctioned person on it
 *
 * Two false proposals and the actual listing dropped. The header above admits
 * the reason in advance — "I have not had a real FIC notice to read" — and what
 * the real one shows is that a notice is not prose with names scattered in it.
 * IT IS A LABELLED RECORD:
 *
 *     A. Individuals
 *     QDi.431 Name: SANAULLAH GHAFARI
 *     DOB: 28/10/1994
 *     Designation: Nationality: Afghanistan Identity no: Passport no: O1503093
 *     Listed on: 21/12/2021
 *
 * The line-shaped heuristic could never have found that name: the line carries
 * a colon, which the "this is a sentence, not a name" rule rejects — correctly,
 * for prose. The structure was the thing to read all along.
 *
 * ---------------------------------------------------------------------------
 * AND READING THE STRUCTURE IS ALSO WHAT KEEPS INNOCENT PEOPLE OFF THE LIST
 *
 * This one page contains three names that are NOT sanctioned persons:
 *
 *     Thabiso Thiti      the Director of the FIC, who signs the notice
 *     Abdul Jabbar       the listed person's father
 *     Abdul Ghaffar      his grandfather
 *
 * Every one of them is two capitalised words and would pass any rule written
 * about capitalisation. The Director of the Financial Intelligence Centre would
 * have been proposed as a sanctioned person on every notice he signs.
 *
 * A `Name:` label cannot make that mistake: the father and grandfather sit
 * behind "Father’s name:" and "Grandfather’s name:", which are not the label
 * `Name`, and the Director appears in running prose behind no label at all. The
 * comparison is exact for that reason and not merely tidy.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS MEASURED HERE AND WHAT IS STILL A GUESS
 *
 * MEASURED, against TFS-922: the labelled-record shape, the `QDi.431` style of
 * UN reference, the `DD/MM/YYYY` dates, and labels sharing one line.
 *
 * STILL A GUESS: the United Nations consolidated list prints names as
 * `Name: 1: SANAULLAH 2: GHAFARI 3: na 4: na`, and a notice carrying that form
 * is handled below without ever having been seen. It is written to degrade into
 * something a person can correct rather than to be trusted.
 *
 * NOTHING HERE WRITES ANYTHING, which is what makes the guess affordable. Every
 * entry goes in front of a compliance officer who ticks, edits and unticks.
 */

/** `QDi.431`, `QDe.161`, `TAi.001` — the UN's own listing reference. */
const UN_REFERENCE = /\b([A-Z][A-Za-z]{1,3}\.\d{1,4}[A-Za-z]?)\b/;

/** `28/10/1994` → `1994-10-28`. Anything else is left alone and dropped. */
function isoDate(raw) {
  const m = String(raw || '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const [, d, mo, y] = m;
  const day = Number(d); const mon = Number(mo);
  if (mon < 1 || mon > 12 || day < 1 || day > 31) return null;
  return `${y}-${String(mon).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Split one line into its `Label: value` pairs, in order.
 *
 * A line carries several — "Designation: Nationality: Afghanistan Identity no:
 * Passport no: O1503093" is four of them and two are empty. Empty is a real
 * answer on an FIC notice and is kept as such, because "Designation:" with
 * nothing after it means the Security Council recorded none, not that the parse
 * failed.
 */
export const NOTICE_LABELS = [
  'Name', 'DOB', 'Date of birth', 'Designation', 'Nationality',
  'Identity no', 'Passport no', 'Listed on', 'Other information',
  'Title', 'Address', 'Good quality a.k.a.', 'Low quality a.k.a.', 'A.k.a.',
  'Old Information', 'New Information', 'ModifiedDate', 'UnListType',
];

/**
 * THE VOCABULARY IS EXPLICIT, AND THE FIRST VERSION GUESSED INSTEAD.
 *
 * It matched "any capitalised run of words before a colon", and measured
 * against TFS-922 that read
 *
 *     Designation: Nationality: Afghanistan Identity no: Passport no: O1503093
 *
 * as designation = "Nationality:" with the nationality lost entirely — because
 * a lazy pattern starting at "Afghanistan" happily swallows "Afghanistan
 * Identity no" as one label. A rule about the SHAPE of a label cannot tell a
 * label from the last words of the value in front of it.
 *
 * A fixed list can. An unrecognised label does not split a line, so it stays
 * inside the value it belongs to — which is the safe direction: a field nobody
 * reads, rather than a value cut in half.
 */
//
// AND THE LEADING SPACE IS CHECKED, NEVER CONSUMED. Written as `(^|\s)Label:`
// first, which cannot split two labels that touch: after "Designation: " the
// pattern's own `\s*` has already eaten the only space, so "Nationality:" has
// nothing in front of it left to match and the value ran on to swallow it —
// "designation: Nationality: Afghanistan", with the nationality lost. Measured
// on TFS-922 twice, once for each version of this mistake.
const LABEL_RE = new RegExp(
  `(${NOTICE_LABELS.map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\s*:\\s*`,
  'gi',
);

export function labelledFields(line) {
  const text = String(line || '');
  const re = new RegExp(LABEL_RE.source, 'gi');
  const marks = [];
  let m = re.exec(text);
  while (m) {
    // A label starts a word. Without this, "Passport no:" would also match the
    // "no:" inside it, and "Title:" would match inside "Subtitle:".
    if (m.index === 0 || /[\s(]/.test(text[m.index - 1])) {
      marks.push({
        label: m[1].trim(),
        from: m.index,
        valueFrom: re.lastIndex,
      });
    }
    m = re.exec(text);
  }
  return marks.map((mark, i) => ({
    label: mark.label,
    head: text.slice(0, mark.from),
    value: text.slice(mark.valueFrom, i + 1 < marks.length ? marks[i + 1].from : undefined).trim(),
  }));
}

/** `1: SANAULLAH 2: GHAFARI 3: na 4: na` → `SANAULLAH GHAFARI`. */
function cleanNameValue(raw) {
  return String(raw || '')
    .replace(/\b\d+\s*:\s*/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !/^(na|n\/a|-|–)$/i.test(w))
    .join(' ')
    .replace(/\s*,\s*$/, '')
    .trim();
}

/**
 * Every listing on a notice, with everything the notice says about it.
 *
 * Returns `[{ fullName, reference, dateOfBirth, nationality, designation }]` —
 * the exact shape addSanctionsListEntries takes, because the notice carries all
 * five and proposing only a name would throw away the evidence that tells two
 * people with one name apart. A date of birth is the difference between holding
 * the right account and holding somebody else's.
 *
 * An entry opens at a `Name:` label and stays open until the next one, so the
 * DOB and nationality on the lines beneath a name belong to that name.
 */
export function entriesFromText(text) {
  const lines = String(text || '').split(/\r?\n/).map((l) => l.replace(/\s+/g, ' ').trim());
  const out = [];
  let current = null;
  const close = () => {
    if (current && current.fullName) out.push(current);
    current = null;
  };

  lines.forEach((line) => {
    if (!line) return;
    const fields = labelledFields(line);
    if (fields.length === 0) return;

    fields.forEach((f, i) => {
      const label = f.label.toLowerCase();

      if (label === 'name') {
        // A `Name:` LABEL IS NOT ENOUGH ON ITS OWN, and TFS-922 is why.
        // "Other information: ... Father’s name: Abdul Jabbar. Grandfather’s
        // name: Abdul Ghaffar." — both end in the word `name` followed by a
        // colon, and both are relatives of the listed person rather than
        // listed people. So the label has to be the WHOLE of what stands
        // before the colon: nothing in front of it but a UN reference.
        const head = String(f.head || '').trim();
        if (head && !UN_REFERENCE.test(head)) return;
        if (head && head.replace(UN_REFERENCE, '').trim() !== '') return;

        close();
        const fullName = cleanNameValue(f.value);
        if (!fullName) return;
        // THE REFERENCE SITS BEFORE THE LABEL ON THE SAME LINE —
        // "QDi.431 Name: SANAULLAH GHAFARI" — so it is read from the head of
        // the line rather than from the value. Only for the first Name on a
        // line; a second listing on one line has no reference of its own to
        // claim, and claiming the first one would attach the wrong reference
        // to a real person.
        const ref = head.match(UN_REFERENCE);
        current = {
          fullName,
          reference: ref ? ref[1] : null,
          dateOfBirth: null,
          nationality: null,
          designation: null,
        };
        return;
      }

      if (!current) return;
      if (label === 'dob' || label === 'date of birth') {
        current.dateOfBirth = current.dateOfBirth || isoDate(f.value);
      } else if (label === 'nationality') {
        current.nationality = current.nationality || (f.value || null);
      } else if (label === 'designation') {
        current.designation = current.designation || (f.value || null);
      } else if (label === 'other information' || label === 'listed on') {
        // Deliberately not captured. "Other information" is a paragraph that
        // frequently contains OTHER PEOPLE'S NAMES — a father, a grandfather —
        // and there is no field on a sanctions entry it belongs in.
      }
    });
  });

  close();
  return out;
}

export function namesFromText(text) {
  const seen = new Set();
  const out = [];
  String(text || '')
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .forEach((line) => {
      if (!looksLikeAName(line)) return;
      const key = line.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(line);
    });
  return out;
}

/**
 * A notice reference, if the document announces one.
 *
 * Offered as a SUGGESTION the officer can overwrite, never used silently: the
 * database requires a notice reference and refuses the upload without one, and
 * a reference guessed wrong is worse than a reference typed.
 */
export function noticeReferenceFromText(text) {
  const m = String(text || '').match(
    /\b((?:FIC|UNSC|TFS)[\s/-]?[A-Z0-9][A-Z0-9/-]{2,})/i);
  return m ? m[1].replace(/\s+/g, ' ').trim() : '';
}

/**
 * Read the text layer out of a PDF.
 *
 * RETURNS THE TEXT, NOT THE NAMES, so the caller can show a person what the
 * document actually said when the extraction finds nothing — which is the
 * single most likely outcome for a scanned notice, and "no names found" with
 * no explanation is the message that makes somebody assume the tool is broken
 * rather than that the document is an image.
 *
 * @param {ArrayBuffer} bytes
 * @returns {Promise<{ text: string, pages: number, hasTextLayer: boolean }>}
 */
export async function readPdfText(bytes) {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
  const worker = await import('pdfjs-dist/legacy/build/pdf.worker.entry');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default || worker;

  // ---- GHSA-wgrm-67xf-hhpq, CLOSED HERE RATHER THAN BY AN UPGRADE --------
  //
  // pdf.js at or below 4.1.392 -- this Portal pins 2.16.105 -- executes
  // JavaScript embedded in a font program when `isEvalSupported` is left at its
  // default of true. `npm audit` rates it HIGH and calls it "arbitrary
  // JavaScript execution upon opening a malicious PDF".
  //
  // IT IS NOT THEORETICAL HERE. This function's entire job is to open a PDF a
  // compliance officer received and believes came from the FIC, inside a
  // signed-in STAFF origin. A spoofed notice is the cheapest way in the
  // building, and script 285's lesson is that the role holding the session is
  // the one that matters.
  //
  // `isEvalSupported: false` disables the eval path the advisory is about and
  // costs this caller nothing: it reads a text layer, and the option only
  // affects font-program compilation. The alternative fix `npm audit` offers is
  // pdfjs-dist@6, a major version with a different module shape -- a breaking
  // change to close a hole one argument already closes.
  const doc = await pdfjs.getDocument({
    data: bytes,
    isEvalSupported: false,
  }).promise;
  const parts = [];
  for (let n = 1; n <= doc.numPages; n += 1) {
    // Sequential on purpose. A notice is a handful of pages and a compliance
    // officer is watching a progress line; parallelising it would save nothing
    // a person could perceive and would make the page order non-deterministic.
    // eslint-disable-next-line no-await-in-loop
    const page = await doc.getPage(n);
    // eslint-disable-next-line no-await-in-loop
    const content = await page.getTextContent();

    // REBUILD THE LINES. A text layer is positioned fragments, not lines: a
    // name can arrive as three items and two names can share a y. Joining
    // everything with spaces would merge two people into one listing, so the
    // items are grouped by their y coordinate first.
    const rows = new Map();
    content.items.forEach((item) => {
      if (typeof item.str !== 'string') return;
      const y = Math.round((item.transform?.[5] ?? 0) * 2) / 2;
      if (!rows.has(y)) rows.set(y, []);
      rows.get(y).push(item);
    });
    [...rows.keys()]
      // Down the page: PDF y grows upwards, so descending y is reading order.
      .sort((a, b) => b - a)
      .forEach((y) => {
        const line = rows.get(y)
          .sort((a, b) => (a.transform?.[4] ?? 0) - (b.transform?.[4] ?? 0))
          .map((i) => i.str)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim();
        if (line) parts.push(line);
      });
  }

  const text = parts.join('\n');
  return {
    text,
    pages: doc.numPages,
    // A SCANNED NOTICE HAS NO TEXT LAYER AT ALL, and that is a different
    // problem from a notice with no names in it. Told apart here so the screen
    // can say which one happened.
    hasTextLayer: text.replace(/\s/g, '').length > 0,
  };
}
